<template>
  <div class="pl-command-center">
    <div v-if="loading" class="text-center p-lg">
      <i class="fa-solid fa-spinner fa-spin text-gray-500"></i>
    </div>
    <template v-else>
      <div class="row">
        <div class="col-md-7">
          <!-- Schedule panel: the same date strip + slot rows the coach home
               uses, so an admin can walk backward/forward through days and jump
               straight into booking a wrestler with a specific coach -->
          <div class="flex flex-wrap align-items-baseline justify-space-between p-t-md">
            <h1 class="m-t-none m-b-none p-b-xxs font-heavy w-fit">
              <span class="hidden-xs">Private Lesson Schedule</span>
              <span class="visible-xs">Schedule</span>
            </h1>
            <a :href="reportPaths.day" class="text-14 font-semi text-green-600">Availability board</a>
          </div>
          <date-strip-picker
            v-model="selectedDate"
            class="m-t-sm"
            :active-dates="activeDates"
            title=""
            :loading-more="loadingMore"
            :allow-past="true"
            :loading-earlier="loadingEarlier"
            @load-more="loadMore"
            @load-earlier="loadEarlier"
          ></date-strip-picker>
          <div v-if="scheduleLoading" class="text-center p-lg">
            <i class="fa-solid fa-spinner fa-spin text-gray-500"></i>
          </div>
          <template v-else>
            <div v-if="selectedDayGroups.length === 0" class="bg-gray-50 rounded-sm p-md text-center">
              <p class="text-gray-600 m-b-none">No lessons or open slots on {{ selectedDate.format('dddd, MMM D') }}.</p>
              <button v-if="nextOpenDate" type="button" class="btn btn-link text-blue-600" @click="selectedDate = nextOpenDate">
                Next day with lessons: {{ nextOpenDate.format('ddd, MMM D') }} &rarr;
              </button>
            </div>
            <!-- Keyed by day+coach so changing days remounts the expanders
                 back to their collapsed-summary default -->
            <div v-for="group in selectedDayGroups" :key="`${selectedDateKey}-${group.coachId}`" class="border-top p-y-m">
              <vertical-expander>
                <template v-slot:title>
                  <p class="m-y-none font-semi">
                    {{ group.coachName }}
                    <span class="font-normal text-gray-600 text-14">{{ group.summary }}</span>
                  </p>
                </template>
                <template v-slot:content>
                  <!-- Margin only exists when expanded, so the header keeps
                       symmetric padding while collapsed and gains matching
                       space above the first row's border when open -->
                  <div class="m-t-m">
                    <availability-slot-row
                      v-for="lessonSlot in group.slots"
                      :key="lessonSlot.id"
                      :lesson-slot="lessonSlot"
                      :confirming-delete-slot-id="confirmingDeleteSlotId"
                      :show-lesson-actions="true"
                      @delete="confirmDelete"
                      @update-status="updateBookingStatus"
                    ></availability-slot-row>
                  </div>
                </template>
              </vertical-expander>
            </div>
          </template>

        </div>

        <div class="col-md-5">
          <!-- Needs attention feed -->
          <div class="flex flex-wrap align-items-baseline justify-space-between p-t-md">
            <h1 class="m-t-none m-b-none p-b-xxs font-heavy w-fit">Needs Attention</h1>
          </div>
          <div v-if="attentionGroups.length === 0" class="bg-gray-50 rounded-sm p-md m-t-sm">
            <p class="text-14 text-gray-600 m-b-none"><i class="fa-solid fa-circle-check text-green-500 m-r-xxs"></i> Nothing needs your attention right now.</p>
          </div>
          <!-- One summary pill per issue type; the detail rows (and the
               checkout list for unpaid dues) expand in place -->
          <div v-else class="flex flex-col gap-y-xs m-t-sm">
            <div v-for="group in attentionGroups" :key="group.key">
              <div
                class="pl-attention-card"
                :class="[`is-${group.level}`, { 'cursor-pointer': group.expandable }]"
                :data-cy="group.dataCy"
                @click="group.expandable && toggleAttention(group.key)">
                <i class="flex-shrink-0" :class="group.icon"></i>
                <div class="flex-1" style="min-width: 0;">
                  <p class="text-14 font-semi m-b-none">{{ group.title }}</p>
                  <p v-if="group.detail" class="text-14 m-b-none pl-attention-detail">{{ group.detail }}</p>
                </div>
                <i v-if="group.expandable" class="flex-shrink-0" :class="attentionOpen(group.key) ? 'fa-solid fa-caret-down' : 'fa-solid fa-caret-right'"></i>
              </div>

              <template v-if="group.expandable && attentionOpen(group.key)">
                <!-- Needs collecting: past lessons without a matching payment,
                     actionable in place via the coach checkout flow -->
                <div v-if="group.key === 'unpaid'" class="pl-attention-items">
                  <div v-for="invoice in visibleUnpaidInvoices" :key="invoice.manual_invoice_id" data-cy="cc-unpaid-row" class="pl-slot-row flex align-items-center gap-sm p-y-m">
                    <div class="flex-1" style="min-width: 0;">
                      <p class="m-b-none font-semi pl-truncate">
                        {{ invoice.wrestler_name }}
                        <span class="font-normal text-gray-600">· {{ dollars(invoice.amount_due_cents) }}</span>
                      </p>
                      <p class="m-b-none text-14 text-gray-600 pl-truncate">{{ invoice.coach_name }} · {{ lessonDate(invoice.lesson_at) }}</p>
                      <!-- Nudge state so two admins don't double-poke the coach -->
                      <p v-if="invoice.coach_reminded_at" class="m-b-none text-12 text-gray-500">
                        Coach reminded {{ remindedAgo(invoice) }}
                      </p>
                    </div>
                    <button
                      v-if="canRemind(invoice)"
                      type="button"
                      class="btn btn-default btn-sm flex-shrink-0"
                      :disabled="remindingInvoiceId === invoice.manual_invoice_id"
                      :data-cy="`cc-remind-coach-${invoice.manual_invoice_id}`"
                      @click="remindCoach(invoice)">
                      Remind coach
                    </button>
                    <a
                      v-if="invoice.private_lesson_id"
                      :href="lessonCheckoutHref(invoice.private_lesson_id)"
                      class="btn btn-primary btn-sm flex-shrink-0">
                      Check out
                    </a>
                  </div>
                  <button
                    v-if="unpaidInvoices.length > 10"
                    type="button"
                    class="btn btn-link btn-compact text-blue-600 p-x-xs m-t-xs"
                    @click="showAllUnpaid = !showAllUnpaid">
                    {{ showAllUnpaid ? 'Show less' : `Show ${unpaidInvoices.length - 10} more` }}
                  </button>
                </div>
                <div v-else class="pl-attention-items">
                  <div v-for="(item, index) in group.items" :key="index" class="p-y-sm" :class="{ 'border-top': index > 0 }">
                    <p class="text-14 font-semi m-b-none">{{ item.title }}</p>
                    <p v-if="item.detail" class="text-14 m-b-none pl-attention-detail">{{ item.detail }}</p>
                    <a v-if="item.href" :href="item.href" class="text-14 font-semi">{{ item.linkText }}</a>
                  </div>
                </div>
              </template>
            </div>
          </div>

        </div>
      </div>

      <!-- Stats + Coach Pay: stats hosts the windowed dashboards (net by coach,
           fill rate, more later) behind one shared date filter; coach pay sits
           beside it on desktop, below on mobile. -->
      <div class="row">
        <div class="col-md-7">
          <div class="flex flex-wrap align-items-baseline justify-space-between p-t-md">
            <h1 class="m-t-none m-b-none p-b-xxs font-heavy w-fit">Stats</h1>
          </div>
          <div v-if="revenue" class="m-t-xs">
            <div class="flex align-items-center gap-x-xs flex-wrap">
              <select v-model="breakdownRange" class="form-control input-sm" style="width: auto;" @change="onBreakdownRangeChange">
                <option v-for="opt in breakdownRangeOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
              </select>
              <template v-if="breakdownRange === 'custom'">
                <input v-model="breakdownStart" type="date" class="form-control input-sm" style="width: auto;">
                <span class="text-12 text-gray-500">to</span>
                <input v-model="breakdownEnd" type="date" class="form-control input-sm" style="width: auto;">
                <button type="button" class="btn btn-primary btn-sm" :disabled="!breakdownStart || !breakdownEnd" @click="applyWindow">Apply</button>
              </template>
              <i v-if="breakdownLoading" class="fa-solid fa-spinner fa-spin text-gray-500"></i>
            </div>
            <!-- Window money at a glance: collected is the top line (by payment
                 date); club kept and paid out are the cash-flow companions from
                 the payroll math, so they don't decompose collected exactly —
                 each tile's info toggle carries its own caveat -->
            <div class="pl-pay-stats flex m-t-m">
              <div class="pl-pay-stat">
                <p class="m-b-none text-20 font-heavy pl-slot-time" data-cy="cc-collected-total">{{ dollars(revenue.breakdown.total_cents) }}</p>
                <p class="m-b-none text-12 text-gray-600">Collected · {{ breakdownRangeLabel }}</p>
              </div>
              <template v-if="revenue.cash_flow">
                <div class="pl-pay-stat">
                  <p class="m-b-none text-20 font-heavy pl-slot-time" data-cy="cc-club-kept">{{ dollars(revenue.cash_flow.facility_cut_cents) }}</p>
                  <p class="m-b-none text-12 text-gray-600">
                    Club kept
                    <VDropdown theme="info-tooltip">
                      <i class="fa-solid fa-circle-info text-gray-400 m-l-xxs" style="cursor: help;"></i>
                      <template v-slot:popper>
                        <div>
                          <p class="header">How is this calculated?</p>
                          <p>The club's share (facility fees) of lessons collected {{ breakdownWindowPhrase }}.</p>
                          <p>
                            Collected counts money by payment date while this follows the payroll math,
                            so the two won't always reconcile exactly in a single window.
                          </p>
                        </div>
                      </template>
                    </VDropdown>
                  </p>
                </div>
                <div class="pl-pay-stat" data-cy="cc-paid-out">
                  <p class="m-b-none text-20 font-heavy pl-slot-time">{{ dollars(revenue.cash_flow.payouts_total_cents) }}</p>
                  <p class="m-b-none text-12 text-gray-600">
                    Paid out · {{ revenue.cash_flow.payouts_count }} {{ revenue.cash_flow.payouts_count === 1 ? 'payout' : 'payouts' }}
                    <VDropdown theme="info-tooltip">
                      <i class="fa-solid fa-circle-info text-gray-400 m-l-xxs" style="cursor: help;"></i>
                      <template v-slot:popper>
                        <div>
                          <p class="header">What counts as paid out?</p>
                          <p>Coach payouts recorded {{ breakdownWindowPhrase }}.</p>
                          <p>
                            A payout can cover lessons from earlier windows, so this is cash out
                            the door — not coach pay earned in the window.
                          </p>
                        </div>
                      </template>
                    </VDropdown>
                  </p>
                </div>
              </template>
            </div>

            <div class="flex flex-wrap align-items-baseline justify-space-between m-t-md">
              <h2 class="text-16 font-semi m-y-none">Net by coach</h2>
            </div>
            <p class="text-14 text-gray-600 m-t-xxs m-b-none">Paid lesson revenue · click a bar for the lesson-type breakdown.</p>
            <p v-if="revenue.breakdown.categories.length === 0" class="text-14 text-gray-600 bg-gray-50 rounded-sm p-md m-t-m m-b-none">
              No paid lessons in this window yet.
            </p>
            <payout-category-chart
              v-else
              :key="breakdownChartKey"
              :categories="revenue.breakdown.categories"
              container-id="pl-revenue-breakdown"
              title=""
              :use-category-colors="false"
            ></payout-category-chart>

            <!-- stats and revenue load independently — a failed admin_stats
                 fetch must not abort the whole render -->
            <template v-if="fillStats">
              <div class="flex flex-wrap align-items-baseline justify-space-between m-t-md">
                <h2 class="text-16 font-semi m-y-none">Fill rate</h2>
              </div>
              <p class="text-14 text-gray-600 m-t-xxs m-b-sm" data-cy="cc-fill-rate">Booked hours over published hours · {{ breakdownRangeLabel }} · {{ percent(fillStats.fill.current.rate) }} team-wide.</p>
              <p v-if="fillStats.coaches.length === 0" class="text-14 text-gray-600 bg-gray-50 rounded-sm p-md m-b-none">
                No published availability in this window.
              </p>
              <div v-else>
                <div v-for="coach in fillStats.coaches" :key="coach.id" class="pl-snapshot-row">
                  <span class="text-14 text-gray-700 pl-truncate">{{ coach.first_name }}</span>
                  <div class="pl-snapshot-track">
                    <div class="pl-snapshot-fill" :style="{ width: `${Math.round((coach.fill_rate || 0) * 100)}%` }"></div>
                  </div>
                  <span class="text-14 text-gray-700 pl-slot-time">
                    {{ percent(coach.fill_rate) }}
                    <span class="text-gray-500">· {{ coach.booked_hours }}/{{ coach.published_hours }}h</span>
                  </span>
                </div>
              </div>
            </template>
          </div>
        </div>

        <div class="col-md-5">
          <div class="flex flex-wrap align-items-baseline justify-space-between p-t-md">
            <h1 class="m-t-none m-b-none p-b-xxs font-heavy w-fit">Coach Pay</h1>
            <a :href="reportPaths.coach_pay" class="text-14 font-semi text-green-600">Pay coaches</a>
          </div>
          <template v-if="payroll">
            <!-- Due amount as a status card: amber to-do while money is owed,
                 quiet settled state otherwise. The whole card goes to payroll. -->
            <a
              v-if="payrollDueCents > 0"
              :href="reportPaths.coach_pay"
              class="pl-attention-card is-warning m-t-xs"
              data-cy="cc-coach-pay-due">
              <i class="fa-solid fa-dollar-sign flex-shrink-0"></i>
              <div class="flex-1" style="min-width: 0;">
                <p class="text-14 font-semi m-b-none">{{ dollars(payrollDueCents) }} due across {{ payrollDueCoachCount }} {{ payrollDueCoachCount === 1 ? 'coach' : 'coaches' }}</p>
                <p class="text-14 m-b-none pl-attention-detail">Record payouts on the coach pay page.</p>
              </div>
              <i class="fa-solid fa-angle-right flex-shrink-0"></i>
            </a>
            <div v-else class="bg-gray-50 rounded-sm p-md m-t-xs" data-cy="cc-coach-pay-due">
              <p class="text-14 text-gray-600 m-b-none"><i class="fa-solid fa-circle-check text-green-500 m-r-xxs"></i> $0 due — all coaches settled up.</p>
            </div>

            <div class="flex flex-wrap align-items-baseline justify-space-between m-t-m">
              <p class="text-14 font-semi m-b-none">Recent payouts</p>
              <a :href="reportPaths.coach_pay" class="text-14 font-semi text-green-600">Full history</a>
            </div>
            <p v-if="payroll.recent_payouts.length === 0" class="text-14 text-gray-600 m-t-xxs m-b-none">No payouts recorded yet.</p>
            <div v-for="payout in payroll.recent_payouts" :key="payout.id" class="border-top m-t-xxs">
              <div class="flex justify-space-between align-items-baseline gap-sm p-y-xs cursor-pointer" @click="togglePayoutLessons(payout)">
                <span class="text-14 text-gray-700 pl-truncate">
                  <i class="m-r-xxs text-gray-500" :class="expandedPayouts[payout.id] ? 'fa-solid fa-caret-down' : 'fa-solid fa-caret-right'"></i>{{ payout.coach_name }}
                </span>
                <span class="text-14 text-gray-700 pl-slot-time flex-shrink-0">{{ dollars(payout.amount_cents) }} · {{ payoutDate(payout.paid_on) }}</span>
              </div>
              <div v-if="expandedPayouts[payout.id]" class="p-b-sm">
                <p v-if="expandedPayouts[payout.id].loading" class="text-14 text-gray-500 m-b-none"><i class="fa-solid fa-spinner fa-spin"></i></p>
                <template v-else>
                  <p v-if="payout.snapshot" class="text-12 text-gray-600 m-b-xs">
                    Lesson breakdown frozen when the payout was recorded &mdash; later refunds or edits don't rewrite it.
                  </p>
                  <p v-else class="text-12 text-gray-600 m-b-xs">
                    Recorded before breakdowns were saved with payouts &mdash; showing today's math for the payout window.
                  </p>
                  <payroll-lesson-table :lessons="expandedPayouts[payout.id].lessons"></payroll-lesson-table>
                </template>
              </div>
            </div>
          </template>
        </div>
      </div>

      <div v-show="error" class="m-t-sm">
        <small><span class="text-danger">{{ errorMessage }}</span></small>
      </div>
    </template>
  </div>
</template>

<script>
import qs from 'qs';
import DateStripPicker from './date_strip_picker.vue';
import AvailabilitySlotRow from './availability_slot_row.vue';
import VerticalExpander from './vertical_expander.vue';
import PayoutCategoryChart from './payout_category_chart.vue';
import PayrollLessonTable from './payroll_lesson_table.vue';
import { slotFromEvent } from './private_lesson_slot_mapper';
import { errorableMix } from './mixins/errorable_mix';
import { lessonCheckoutMix, privateLessonBookingMix } from './mixins/private_lesson_booking_mix';
import { currencyMix } from '../../shared/currency_mix';

// Admin command center for private lessons: replaces the all-coach FullCalendar
// on /private_lessons/dashboard. One page, four jobs — a browsable all-coach
// schedule, coach performance, revenue, and a needs-attention feed — each
// linking to a deeper report page.
export default {
  name: 'private-lesson-command-center',
  components: {
    DateStripPicker,
    AvailabilitySlotRow,
    VerticalExpander,
    PayoutCategoryChart,
    PayrollLessonTable,
  },
  mixins: [errorableMix, lessonCheckoutMix, privateLessonBookingMix, currencyMix],
  props: {
    reportPaths: {
      // { day, coach_pay }
      type: Object,
      required: true,
    },
    // The admin's own coach profile id, recorded as booked_by when they
    // confirm a booking from here. Null for non-coach billing admins.
    currentProfileId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      loading: true,
      stats: null,
      // admin_stats refetched for the stats-section window; null until the
      // filter moves off the default (which matches stats' own 30 days)
      statsWindow: null,
      revenue: null,
      payroll: null,
      scheduleLoading: true,
      scheduleEvents: [],
      selectedDate: moment(),
      // The strip starts with 30 visible days; keep our fetch window in sync
      loadedThrough: moment().add(30, 'days').endOf('day'),
      loadedBackTo: moment().startOf('day'),
      loadingMore: false,
      loadingEarlier: false,
      confirmingDeleteSlotId: null,
      breakdownRange: '30',
      breakdownStart: '',
      breakdownEnd: '',
      breakdownLoading: false,
      showAllUnpaid: false,
      openAttentionKeys: [],
      // payout_id -> { loading, lessons } for an expanded recent payout
      expandedPayouts: {},
      remindingInvoiceId: null,
    };
  },
  computed: {
    breakdownRangeOptions() {
      return [
        { label: '30-Day', value: '30' },
        { label: '60-Day', value: '60' },
        { label: '90-Day', value: '90' },
        { label: '180-Day', value: '180' },
        { label: 'Custom', value: 'custom' },
      ];
    },
    breakdownRangeLabel() {
      if (this.breakdownRange === 'custom') {
        if (this.breakdownStart && this.breakdownEnd) {
          return `${moment(this.breakdownStart).format('MMM D')} – ${moment(this.breakdownEnd).format('MMM D')}`;
        }
        return 'Custom';
      }
      const match = this.breakdownRangeOptions.find((opt) => opt.value === this.breakdownRange);
      return match ? match.label : '';
    },
    unpaidInvoices() {
      return this.revenue ? this.revenue.unpaid.invoices : [];
    },
    // Keep the column glanceable by default; the toggle reveals the full list
    visibleUnpaidInvoices() {
      return this.showAllUnpaid ? this.unpaidInvoices : this.unpaidInvoices.slice(0, 10);
    },
    // Fill data for the stats section: the windowed refetch when the filter
    // has moved, otherwise the page's default 30-day stats
    fillStats() {
      return this.statsWindow || this.stats;
    },
    payrollDueCents() {
      if (!this.payroll) {
        return 0;
      }
      return this.payroll.coaches.reduce((sum, row) => sum + (row.owed.coach_pay || 0), 0);
    },
    payrollDueCoachCount() {
      if (!this.payroll) {
        return 0;
      }
      return this.payroll.coaches.filter((row) => (row.owed.coach_pay || 0) > 0).length;
    },
    // Remounts the chart when the window changes — highchart-wrapper only
    // builds its chart on mounted()
    breakdownChartKey() {
      const breakdown = this.revenue && this.revenue.breakdown;
      return breakdown ? `${breakdown.start_date}-${breakdown.end_date}-${breakdown.total_cents}` : 'empty';
    },
    // Raw events -> slot rows in the shape availability-slot-row expects.
    // Unlike the coach agenda, cancelled/declined slots are dropped — the
    // admin schedule is a live board, not a per-coach record.
    scheduleSlots() {
      return this.scheduleEvents
        .filter((event) => event.event_type === 'private_lesson')
        .map((event) => slotFromEvent(event))
        .filter((lessonSlot) => lessonSlot !== null && lessonSlot.status !== 'cancelled' && lessonSlot.status !== 'declined')
        .sort((a, b) => a.startAt - b.startAt);
    },
    activeDates() {
      return [...new Set(this.scheduleSlots.map((s) => s.startAt.format('YYYY-MM-DD')))];
    },
    selectedDateKey() {
      return this.selectedDate.format('YYYY-MM-DD');
    },
    // The selected day's slots grouped by coach, alphabetical, each with a
    // status-count summary for the collapsed glance
    selectedDayGroups() {
      const groups = {};
      this.scheduleSlots
        .filter((s) => s.startAt.isSame(this.selectedDate, 'day'))
        .forEach((lessonSlot) => {
          if (!groups[lessonSlot.coachId]) {
            groups[lessonSlot.coachId] = { coachId: lessonSlot.coachId, coachName: lessonSlot.coachName, slots: [] };
          }
          groups[lessonSlot.coachId].slots.push(lessonSlot);
        });
      return Object.values(groups)
        .map((group) => ({ ...group, summary: this.groupSummary(group.slots) }))
        .sort((a, b) => a.coachName.localeCompare(b.coachName));
    },
    // The nearest loaded day after the selection with slots, so an empty day
    // offers a one-tap jump instead of a hunt through the strip
    nextOpenDate() {
      const selectedKey = this.selectedDate.format('YYYY-MM-DD');
      const nextKey = [...this.activeDates].sort().find((key) => key > selectedKey);
      return nextKey ? moment(nextKey, 'YYYY-MM-DD') : null;
    },
    // Each issue type collapses to one severity pill (title + who/how much);
    // the per-coach or per-lesson rows only render once the pill is expanded
    attentionGroups() {
      const groups = [];

      // Money first: uncollected dues always lead the feed, then coach
      // supply problems by severity, then the neutral nudges
      if (this.revenue && this.unpaidInvoices.length > 0) {
        groups.push({
          key: 'unpaid',
          dataCy: 'cc-unpaid-total',
          level: 'serious',
          icon: 'fa-solid fa-dollar-sign',
          expandable: true,
          title: `${this.dollars(this.revenue.unpaid.total_cents)} in unpaid lesson dues.`,
          detail: `${this.revenue.unpaid.count} ${this.revenue.unpaid.count === 1 ? 'lesson' : 'lessons'} that already happened.`,
        });
      }

      if (this.stats) {
        const lowFill = this.stats.coaches.filter((c) => c.low_fill);
        if (lowFill.length > 0) {
          groups.push({
            key: 'fill',
            dataCy: 'cc-attention-fill',
            level: 'serious',
            icon: 'fa-solid fa-triangle-exclamation',
            expandable: true,
            title: `Fill rate issues for ${lowFill.length} ${lowFill.length === 1 ? 'coach' : 'coaches'}.`,
            detail: `Last ${this.stats.period_days} days · ${lowFill.map((c) => c.first_name).join(', ')}`,
            items: lowFill.map((coach) => ({
              title: `${coach.first_name} ${coach.last_name}'s fill rate is ${this.percent(coach.fill_rate)}.`,
              detail: `${coach.published_hours} published hours, ${coach.booked_hours} booked in the last ${this.stats.period_days} days.`,
              href: `/private_lessons/coaches/${coach.id}/dashboard`,
              linkText: 'See coach',
            })),
          });
        }
        const unpublished = this.stats.coaches.filter((c) => c.nothing_published_14d);
        if (unpublished.length > 0) {
          groups.push({
            key: 'unpublished',
            dataCy: 'cc-attention-unpublished',
            level: 'warning',
            icon: 'fa-regular fa-clock',
            expandable: true,
            title: `${unpublished.length === 1 ? '1 coach has' : `${unpublished.length} coaches have`} nothing published for 14 days.`,
            detail: unpublished.map((c) => c.first_name).join(', '),
            items: unpublished.map((coach) => ({
              title: `${coach.first_name} ${coach.last_name} has nothing published for 14 days.`,
              detail: 'Families searching for lessons won\'t see them at all.',
              href: `/private_lessons/coaches/${coach.id}/dashboard`,
              linkText: 'See coach',
            })),
          });
        }

        if (this.stats.stale_invites.length > 0) {
          groups.push({
            key: 'invites',
            dataCy: 'cc-attention-invites',
            level: 'neutral',
            icon: 'fa-regular fa-bell',
            expandable: false,
            title: `${this.stats.stale_invites.length} ${this.stats.stale_invites.length === 1 ? 'invite has' : 'invites have'} sat unanswered 3+ days.`,
            detail: this.stats.stale_invites.map((i) => i.wrestler_name).slice(0, 3).join(', '),
          });
        }
      }

      return groups;
    },
  },
  mounted() {
    this.fetchAll();
    this.refreshSchedule();
    this.$notificationManager.on('event-deleted', this.eventDeleted);
    this.$notificationManager.on('hide-delete-event-form', this.hideDeleteForm);
  },
  destroyed() {
    this.$notificationManager.off('event-deleted', this.eventDeleted);
    this.$notificationManager.off('hide-delete-event-form', this.hideDeleteForm);
  },
  methods: {
    fetchAll() {
      const vm = this;
      vm.fetchRevenue();
      vm.fetchPayroll();
      axios.get(vm.$apiService.privateLessonAdminStatsUrl())
        .then((statsResponse) => {
          vm.stats = statsResponse.data;
          vm.loading = false;
        })
        .catch((fetchError) => {
          vm.loading = false;
          vm.errorMessage = vm.parseErrorResponse(fetchError);
          vm.error = true;
        });
    },
    // Refetched on its own when the breakdown window changes; handles its own
    // errors so a failed refetch doesn't blank the rest of the page
    fetchRevenue() {
      const vm = this;
      vm.breakdownLoading = true;
      return axios.get(vm.$apiService.privateLessonRevenueStatsUrl(), { params: vm.breakdownParams() })
        .then((response) => {
          vm.revenue = response.data;
        })
        .catch((fetchError) => {
          vm.errorMessage = vm.parseErrorResponse(fetchError);
          vm.error = true;
        })
        .then(() => {
          vm.breakdownLoading = false;
        });
    },
    fetchPayroll() {
      const vm = this;
      axios.get(vm.$apiService.privateLessonPayrollSummaryUrl())
        .then((response) => {
          vm.payroll = response.data;
        })
        .catch((fetchError) => {
          vm.errorMessage = vm.parseErrorResponse(fetchError);
          vm.error = true;
        });
    },
    toggleAttention(key) {
      const index = this.openAttentionKeys.indexOf(key);
      if (index === -1) {
        this.openAttentionKeys.push(key);
      } else {
        this.openAttentionKeys.splice(index, 1);
      }
    },
    attentionOpen(key) {
      return this.openAttentionKeys.includes(key);
    },
    payoutDate(paidOn) {
      return paidOn ? moment(paidOn).format('MMM D') : '—';
    },
    // The server throttles repeat reminders (20h); mirror that window here so
    // the button disappears instead of round-tripping to a 422
    canRemind(invoice) {
      if (!invoice.coach_reminded_at) {
        return true;
      }
      return moment(invoice.coach_reminded_at).isBefore(moment().subtract(20, 'hours'));
    },
    remindedAgo(invoice) {
      return moment(invoice.coach_reminded_at).fromNow();
    },
    remindCoach(invoice) {
      const vm = this;
      if (vm.remindingInvoiceId) {
        return;
      }
      vm.remindingInvoiceId = invoice.manual_invoice_id;
      axios.post(vm.$apiService.privateLessonRemindCollectionUrl(), { manual_invoice_id: invoice.manual_invoice_id })
        .then((response) => {
          vm.remindingInvoiceId = null;
          // coach_reminded_at ships (as null) in the unpaid payload, so this
          // assignment is reactive without a refetch
          invoice.coach_reminded_at = response.data.coach_reminded_at;
          vm.$notificationManager.emit('show-toast', `Reminder sent to ${invoice.coach_name}`, true);
        })
        .catch((remindError) => {
          vm.remindingInvoiceId = null;
          vm.errorMessage = vm.parseErrorResponse(remindError);
          vm.error = true;
        });
    },
    // Same snapshot-or-live pattern as payroll_coach_row: frozen entries when
    // the payout saved them, live recompute of the payout window otherwise
    togglePayoutLessons(payout) {
      const vm = this;
      if (vm.expandedPayouts[payout.id]) {
        vm.$delete(vm.expandedPayouts, payout.id);
        return;
      }
      if (payout.snapshot) {
        vm.$set(vm.expandedPayouts, payout.id, { loading: false, lessons: payout.snapshot.entries });
        return;
      }
      vm.$set(vm.expandedPayouts, payout.id, { loading: true, lessons: [] });
      const url = vm.$apiService.privateLessonPayrollLessonsUrl(payout.coach_profile_id, payout.period_start_at, payout.period_end_at);
      axios.get(url)
        .then((response) => {
          vm.$set(vm.expandedPayouts, payout.id, { loading: false, lessons: response.data.lessons });
        })
        .catch((fetchError) => {
          vm.$delete(vm.expandedPayouts, payout.id);
          vm.errorMessage = vm.parseErrorResponse(fetchError);
          vm.error = true;
        });
    },
    lessonDate(lessonAt) {
      return lessonAt ? moment(lessonAt).format('MMM D') : '—';
    },
    breakdownParams() {
      if (this.breakdownRange === 'custom') {
        return { breakdown_start: this.breakdownStart, breakdown_end: this.breakdownEnd };
      }
      const days = parseInt(this.breakdownRange, 10);
      return {
        breakdown_start: moment().subtract(days, 'days').format('YYYY-MM-DD'),
        breakdown_end: moment().format('YYYY-MM-DD'),
      };
    },
    onBreakdownRangeChange() {
      // Custom waits for both dates + Apply
      if (this.breakdownRange !== 'custom') {
        this.applyWindow();
      }
    },
    // The stats-section filter drives both charts: revenue breakdown and the
    // windowed fill-rate refetch
    applyWindow() {
      this.fetchRevenue();
      this.fetchStatsWindow();
    },
    fetchStatsWindow() {
      const vm = this;
      const params = vm.breakdownParams();
      axios.get(vm.$apiService.privateLessonAdminStatsUrl(), {
        params: { window_start: params.breakdown_start, window_end: params.breakdown_end },
      })
        .then((response) => {
          vm.statsWindow = response.data;
        })
        .catch((fetchError) => {
          vm.errorMessage = vm.parseErrorResponse(fetchError);
          vm.error = true;
        });
    },
    // All-coach schedule for the strip: same fetch shape as the coach agenda,
    // minus the coach_ids filter
    fetchScheduleRange(start, end) {
      const params = {
        event_type: 'private_lesson',
        start: start.format('YYYY-MM-DD'),
        end: end.format('YYYY-MM-DD'),
        expand: ['event_bookings', 'private_lessons'],
      };
      const url = this.$apiService.eventsUrl(`?${qs.stringify(params, { arrayFormat: 'brackets' })}`);
      return this.$apiService.loadAllPages(url, 'events');
    },
    refreshSchedule() {
      const vm = this;
      vm.scheduleLoading = true;
      vm.fetchScheduleRange(vm.loadedBackTo, vm.loadedThrough)
        .then((events) => {
          vm.scheduleEvents = events;
          vm.scheduleLoading = false;
        })
        .catch((fetchError) => {
          vm.scheduleLoading = false;
          vm.errorMessage = vm.parseErrorResponse(fetchError);
          vm.error = true;
        });
    },
    loadMore({ startAt, endAt }) {
      const vm = this;
      vm.loadingMore = true;
      vm.fetchScheduleRange(startAt, endAt)
        .then((events) => {
          const known = new Set(vm.scheduleEvents.map((e) => e.id));
          vm.scheduleEvents = vm.scheduleEvents.concat(events.filter((e) => !known.has(e.id)));
          if (endAt.isAfter(vm.loadedThrough)) {
            vm.loadedThrough = endAt;
          }
          vm.loadingMore = false;
        })
        .catch((fetchError) => {
          vm.loadingMore = false;
          vm.errorMessage = vm.parseErrorResponse(fetchError);
          vm.error = true;
        });
    },
    loadEarlier({ startAt, endAt }) {
      const vm = this;
      vm.loadingEarlier = true;
      vm.fetchScheduleRange(startAt, endAt)
        .then((events) => {
          const known = new Set(vm.scheduleEvents.map((e) => e.id));
          vm.scheduleEvents = vm.scheduleEvents.concat(events.filter((e) => !known.has(e.id)));
          if (startAt.isBefore(vm.loadedBackTo)) {
            vm.loadedBackTo = startAt;
          }
          vm.loadingEarlier = false;
        })
        .catch((fetchError) => {
          vm.loadingEarlier = false;
          vm.errorMessage = vm.parseErrorResponse(fetchError);
          vm.error = true;
        });
    },
    // Admin confirm/decline on a coach's invited booking; the admin's own
    // coach profile is recorded as booked_by
    updateBookingStatus({ lessonSlot, status }) {
      const vm = this;
      vm.updateCoachBookingStatus({
        coachBookingId: lessonSlot.coachBookingId,
        status,
        actorProfileId: vm.currentProfileId,
        onSuccess() {
          vm.refreshSchedule();
          vm.fetchAll();
        },
      });
    },
    // "3 open · 2 booked · 1 to confirm" for a coach's collapsed day row
    groupSummary(slots) {
      const counts = {
        pending: 0, confirmed: 0, invited: 0, completed: 0,
      };
      slots.forEach((s) => {
        if (counts[s.status] !== undefined) {
          counts[s.status] += 1;
        }
      });
      const parts = [];
      if (counts.pending > 0) {
        parts.push(`${counts.pending} open`);
      }
      if (counts.confirmed > 0) {
        parts.push(`${counts.confirmed} booked`);
      }
      if (counts.invited > 0) {
        parts.push(`${counts.invited} to confirm`);
      }
      if (counts.completed > 0) {
        parts.push(`${counts.completed} completed`);
      }
      return parts.join(' · ');
    },
    confirmDelete(lessonSlot) {
      this.confirmingDeleteSlotId = lessonSlot.id;
    },
    hideDeleteForm() {
      this.confirmingDeleteSlotId = null;
    },
    eventDeleted() {
      // Recurring picks (future/all) can remove siblings we have loaded, so
      // refetch the window rather than surgically removing one event
      this.confirmingDeleteSlotId = null;
      this.refreshSchedule();
    },
    percent(rate) {
      if (rate === null || rate === undefined) {
        return '—';
      }
      return `${Math.round(rate * 100)}%`;
    },
    // Whole dollars — command center numbers are glanceable aggregates
    dollars(cents) {
      return this.formatAsCurrency(cents, 0, 0);
    },
  },
};
</script>
